// src/views/whatsappInbox/components/MediaPreview.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'antd';
import { X, ChevronLeft, ChevronRight, Maximize, Minimize, ZoomIn, ZoomOut, RotateCcw, RotateCw, FileText, Download } from 'lucide-react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import {
  selectPreviewOpen,
  selectMediaPreviewUrl,
  selectMediaPreviewType,
  selectMediaPreviewMimeType,
  selectPreviewMessageId,
    closeMediaPreview,
  openMediaPreview,
  selectMediaCache
} from '../../../store/slices/mediaSlice';
import { selectMessages } from '../../../store/slices/messagesSlice';
import { getMediaNavigationStats } from '../utils/mediaGroupingUtil';
import { Logger } from '../../../utils/logger';



// Styled components
const PreviewModal = styled(Modal)<{ isFullScreen?: boolean }>`
  .ant-modal-content {
    background: transparent;
    box-shadow: none;
    padding: 0;
    ${props => props.isFullScreen && `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100vw !important;
      height: 100vh !important;
      max-width: 100vw !important;
      margin: 0;
    `}
  }
  
  .ant-modal-body {
    ${props => props.isFullScreen && `
      height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.9);
    `}
  }
  
  .ant-modal-close {
    top: 16px;
    right: 16px;
    color: white;
    background: rgba(0,0,0,0.5);
    border-radius: 50%;
    padding: 8px;
    z-index: 1000;

    &:hover {
      background: rgba(0,0,0,0.7);
    }
  }

  .preview-content {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    position: relative;
    
    &.fullscreen {
      width: 100vw;
      height: 100vh;
      background: black;
    }
    
    &.document {
      background: white;
      border-radius: 8px;
      padding: 24px;
      max-height: 90vh;
      overflow-y: auto;
    }
  }
`;

const MediaCounter = styled.div`
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
  z-index: 1001;
`;

const Controls = styled.div`
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 16px;
  z-index: 1001;
`;

const ControlButton = styled.button`
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NavigationButton = styled(ControlButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1001;
  
  &.prev {
    left: 16px;
  }
  
  &.next {
    right: 16px;
  }
`;

const ThumbnailStrip = styled.div`
  position: absolute;
  bottom: 70px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 8px;
  overflow-x: auto;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
`;

const Thumbnail = styled.div<{ active: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid ${props => props.active ? 'white' : 'transparent'};
  opacity: ${props => props.active ? '1' : '0.7'};
  cursor: pointer;
  
  &:hover {
    opacity: 1;
  }
  
  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const EnhancedAudioPlayer = styled.div`
  width: 100%;
  max-width: 500px;
  background: rgba(255, 255, 255, 0.1);
  padding: 16px;
  border-radius: 12px;
  
  .audio-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
  }
  
  .audio-progress {
    width: 100%;
    height: 6px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    overflow: hidden;
    position: relative;
    cursor: pointer;
    
    .progress-bar {
      height: 100%;
      background: #0084ff;
      position: absolute;
      left: 0;
      top: 0;
    }
  }
  
  .time-display {
    color: white;
    font-size: 12px;
    margin-top: 8px;
    display: flex;
    justify-content: space-between;
  }
`;

export const MediaPreview: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectPreviewOpen);
  const mediaUrl = useAppSelector(selectMediaPreviewUrl);
  const mediaCache = useAppSelector(selectMediaCache);
  const previewMessageId = useAppSelector(selectPreviewMessageId);
  const mediaType = useAppSelector(selectMediaPreviewType);
  const mimeType = useAppSelector(selectMediaPreviewMimeType);
  const currentMessageId = useAppSelector(selectPreviewMessageId);
  
  const allMessages = useAppSelector(selectMessages);
  
  const [groupContext, setGroupContext] = useState<{
    groupId: string;
    messageIds: string[];
  }>({
    groupId: '',
    messageIds: []
  });

  
  // State for enhanced controls
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = React.useRef<HTMLAudioElement>(null);
  

  useEffect(() => {
    Logger.log("MediaPreview - Preview data:", {
      previewMessageId,
      hasMediaUrl: !!mediaUrl,
      cacheKeysCount: Object.keys(mediaCache).length,
      cacheKeys: Object.keys(mediaCache)
    });
    
    // Check if we can find the URL directly in the cache
    if (previewMessageId) {
      const directCacheCheck = mediaCache[previewMessageId];
      Logger.log("Direct cache check result:", directCacheCheck);
    }
  }, [previewMessageId, mediaUrl, mediaCache]);

  // Debug logging
  useEffect(() => {
    Logger.log("MediaPreview - Component mounted or updated", {
      isOpen,
      mediaType,
      currentMessageId,
      groupSize: groupContext?.messageIds?.length || 0
    });
    
    return () => {
      Logger.log("MediaPreview - Component unmounting");
    };
  }, [isOpen, mediaType, currentMessageId, groupContext]);
  
  // Reset state when preview changes
  useEffect(() => {
    if (currentMessageId) {
      Logger.log("MediaPreview - New message preview:", currentMessageId);
      setZoomLevel(1);
      setRotation(0);
      setAudioProgress(0);
      setAudioCurrentTime(0);
      setAudioDuration(0);
    }
  }, [currentMessageId]);
  
  const handleClose = useCallback(() => {
    Logger.log("MediaPreview - Closing preview");
    
    // If in full screen, exit first
    if (isFullScreen) {
      setIsFullScreen(false);
    }
    
    // Reset all states
    setZoomLevel(1);
    setRotation(0);
    setAudioProgress(0);
    
    // Dispatch close action
    dispatch(closeMediaPreview());
  }, [dispatch, isFullScreen]);
  

  
  const toggleFullScreen = useCallback(() => {
    const newState = !isFullScreen;
    setIsFullScreen(newState);
    Logger.log("MediaPreview - Toggled fullscreen:", newState);
    
    // If exiting fullscreen, reset zoom and rotation
    if (!newState) {
      setZoomLevel(1);
      setRotation(0);
    }
  }, [isFullScreen]);
  
  const handleZoom = useCallback((direction: 'in' | 'out') => {
    const delta = direction === 'in' ? 0.25 : -0.25;
    const newZoom = Math.max(0.5, Math.min(3, zoomLevel + delta));
    setZoomLevel(newZoom);
    Logger.log("MediaPreview - Zoom set to:", newZoom);
  }, [zoomLevel]);
  
  const handleRotate = useCallback((direction: 'cw' | 'ccw') => {
    const delta = direction === 'cw' ? 90 : -90;
    const newRotation = (rotation + delta) % 360;
    setRotation(newRotation);
    Logger.log("MediaPreview - Rotation set to:", newRotation);
  }, [rotation]);
  

  
  // Audio player time update handler
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setAudioProgress(progress);
      setAudioCurrentTime(audioRef.current.currentTime);
    }
  }, []);
  
  // Audio player duration change handler
  const handleDurationChange = useCallback(() => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  }, []);
  
  // Format time for audio player
  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  }, []);
  
  // Handle audio progress bar click
  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current) {
      const progressBar = e.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const position = (e.clientX - rect.left) / progressBar.clientWidth;
      audioRef.current.currentTime = position * audioRef.current.duration;
    }
  }, []);
  
  // Handle thumbnail click
  const handleThumbnailClick = useCallback((messageId: string) => {
    Logger.log("MediaPreview - Thumbnail clicked:", messageId);
    dispatch(openMediaPreview(messageId));
  }, [dispatch]);
  
  // Render thumbnails for group navigation
  const renderThumbnails = useCallback(() => {
    if (!groupContext?.messageIds?.length || groupContext.messageIds.length <= 1) {
      return null;
    }
    
    Logger.log("MediaPreview - Rendering thumbnails for group:", groupContext.groupId);
    
    return (
      <ThumbnailStrip>
        {groupContext.messageIds.map((messageId) => {
          const message = allMessages.find(msg => msg._id === messageId);
          if (!message) return null;
          
          const isCurrentMessage = messageId === currentMessageId;
          const thumbnailUrl = message.downloadUrl;
          
          return (
            <Thumbnail 
              key={messageId} 
              active={isCurrentMessage}
              onClick={() => handleThumbnailClick(messageId)}
            >
              {message.messageType === 'image' && thumbnailUrl && (
                <img src={thumbnailUrl} alt="Thumbnail" />
              )}
              {message.messageType === 'video' && thumbnailUrl && (
                <video src={thumbnailUrl} />
              )}
              {(!thumbnailUrl || !['image', 'video'].includes(message.messageType || '')) && (
                <div className="placeholder">{message.messageType?.charAt(0).toUpperCase()}</div>
              )}
            </Thumbnail>
          );
        })}
      </ThumbnailStrip>
    );
  }, [groupContext, currentMessageId, allMessages, handleThumbnailClick]);
  
  // Get navigation statistics
// Get navigation statistics
const getNavStats = useCallback(() => {
  if (!currentMessageId) {
    return { current: 1, total: 1 };
  }
  
  // Count all media messages
  const mediaMessages = allMessages.filter(msg => 
    msg.messageType === 'image' || 
    msg.messageType === 'video' || 
    msg.messageType === 'audio'
  );
  
  const currentIndex = mediaMessages.findIndex(msg => msg._id === currentMessageId);
  if (currentIndex === -1) {
    return { current: 1, total: mediaMessages.length };
  }
  
  return {
    current: currentIndex + 1,
    total: mediaMessages.length
  };
}, [currentMessageId, allMessages]);
  
  // Render content based on media type
  const renderContent = useCallback(() => {
    if (!mediaUrl) {
      Logger.log("MediaPreview - No media URL available");
      return <div>No preview available</div>;
    }
    
    const { current, total } = getNavStats();
    Logger.log("MediaPreview - Rendering content:", { 
      type: mediaType, 
      hasUrl: !!mediaUrl,
      current,
      total
    });
    
    switch (mediaType) {
      case 'image':
        return (
          <div className={`preview-content ${isFullScreen ? 'fullscreen' : ''}`}>
            <img 
              src={mediaUrl}
              alt="Preview"
              style={{
                maxHeight: isFullScreen ? '100vh' : '90vh',
                maxWidth: isFullScreen ? '100vw' : '90vw',
                objectFit: 'contain',
                transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                transition: 'transform 0.2s ease-out'
              }}
            />
            
          
            
            {/* Image controls */}
            <Controls>
              <ControlButton onClick={() => handleZoom('out')}>
                <ZoomOut size={20} />
              </ControlButton>
              
              <ControlButton onClick={() => handleZoom('in')}>
                <ZoomIn size={20} />
              </ControlButton>
              
              <ControlButton onClick={() => handleRotate('ccw')}>
                <RotateCcw size={20} />
              </ControlButton>
              
              <ControlButton onClick={() => handleRotate('cw')}>
                <RotateCw size={20} />
              </ControlButton>
              
              <ControlButton onClick={toggleFullScreen}>
                {isFullScreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </ControlButton>
            </Controls>
            
            <ControlButton onClick={() => {
  if (mediaUrl) {
    const currentMessage = allMessages.find(msg => msg._id === currentMessageId);
    const a = document.createElement('a');
    a.href = mediaUrl;
    a.download = currentMessage?.message?.filename || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}}>
  <Download size={20} />
</ControlButton>

            {/* Thumbnails for group navigation */}
            {renderThumbnails()}
          </div>
        );
      
      case 'video':
        return (
          <div className={`preview-content ${isFullScreen ? 'fullscreen' : ''}`}>
            <video
              src={mediaUrl}
              controls
              autoPlay
              style={{
                maxHeight: isFullScreen ? '100vh' : '90vh',
                maxWidth: isFullScreen ? '100vw' : '90vw'
              }}
            />
            
           
            
            {/* Video controls */}
            <Controls>
              <ControlButton onClick={toggleFullScreen}>
                {isFullScreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </ControlButton>
            </Controls>
            
            {/* Thumbnails for group navigation */}
            {renderThumbnails()}
          </div>
        );
      
      case 'audio':
        return (
          <div className="preview-content">
            <EnhancedAudioPlayer>
              <audio 
                ref={audioRef}
                src={mediaUrl} 
                onTimeUpdate={handleTimeUpdate}
                onDurationChange={handleDurationChange}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                autoPlay
                hidden
              />
              
              <div className="audio-waveform">
                {/* Waveform visualization would go here */}
                <div 
                  className="audio-progress"
                  onClick={handleProgressClick}
                >
                  <div 
                    className="progress-bar" 
                    style={{ width: `${audioProgress}%` }}
                  />
                </div>
                <div className="time-display">
                  <span>{formatTime(audioCurrentTime)}</span>
                  <span>{formatTime(audioDuration)}</span>
                </div>
              </div>
              
              <div className="audio-controls">
                <ControlButton onClick={() => {
                  if (audioRef.current) {
                    if (isPlaying) {
                      audioRef.current.pause();
                    } else {
                      audioRef.current.play();
                    }
                  }
                }}>
                  {isPlaying ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="6" y="4" width="4" height="16" />
                      <rect x="14" y="4" width="4" height="16" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                  )}
                </ControlButton>
                
                {/* Playback speed control */}
                <ControlButton onClick={() => {
                  if (audioRef.current) {
                    const speeds = [0.5, 1, 1.5, 2];
                    const currentSpeed = audioRef.current.playbackRate;
                    const currentIndex = speeds.indexOf(currentSpeed);
                    const nextIndex = (currentIndex + 1) % speeds.length;
                    audioRef.current.playbackRate = speeds[nextIndex];
                    Logger.log("Audio playback speed set to:", speeds[nextIndex]);
                  }
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13 2L3 14h9l-1 8 10-16h-9l1-4z" />
                  </svg>
                </ControlButton>
              </div>
            </EnhancedAudioPlayer>
            
          
          
          </div>
        );
      
      case 'document':
        if (mimeType?.includes('pdf')) {
          return (
            <div className="preview-content document">
              <iframe
                src={mediaUrl}
                style={{
                  width: '100%',
                  height: '80vh',
                  border: 'none'
                }}
                title="Document Preview"
              />
            </div>
          );
        }
        return (
          <div className="preview-content document">
            <div className="download-prompt">
              <FileText size={48} className="mb-4" />
              <h3>Document</h3>
              <a 
                href={mediaUrl}
                download
                className="download-button"
                onClick={(e) => e.stopPropagation()}
              >
                Download File
              </a>
            </div>
          </div>
        );
    }
    
    return <div>Unsupported media type</div>;
  }, [
    mediaUrl, 
    mediaType, 
    mimeType, 
    isFullScreen,
    zoomLevel,
    rotation,
    audioProgress,
    audioCurrentTime,
    audioDuration,
    isPlaying,
    handleTimeUpdate,
    handleDurationChange,
    formatTime,
    handleProgressClick,
    toggleFullScreen,
    handleZoom,
    handleRotate,
  
    getNavStats,
    renderThumbnails
  ]);
  
  // Check if we should render
  if (!mediaUrl) {
    Logger.log("MediaPreview - No media URL, not rendering");
    return null;
  }
  
  return (
    <PreviewModal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width="auto"
      centered
      closeIcon={<X className="text-white" />}
      isFullScreen={isFullScreen}
    >
      {renderContent()}
    </PreviewModal>
  );
};

export default MediaPreview;